/**
 * What a bot's answer shows besides its text: links into the app (opened in place,
 * shown as the thing they point to) and what the run made (documents it wrote, files
 * it saved on the computer), as cards under the answer.
 */

const DOCUMENT = /^\/app\/documents\/([^/?#]+)/;

/**
 * A link into BusyBee.Day: { kind, to } with the in-app path to open, or null for a
 * link elsewhere. Bots link with absolute addresses (they work in notifications and
 * e-mails too), so the app's own origins count as in-app, and so does a bare path.
 */
export const appLink = (href, origins = []) => {
  if (!href || typeof href !== 'string' || /^(mailto|tel):/i.test(href)) return null;
  let url;
  try {
    url = new URL(href, origins[0] || 'http://localhost');
  } catch {
    return null;
  }
  const relative = href.startsWith('/') && !href.startsWith('//');
  if (!relative && !origins.includes(url.origin)) return null;
  if (url.pathname !== '/app' && !url.pathname.startsWith('/app/')) return null;
  const to = `${url.pathname}${url.search}${url.hash}`;
  const kind = DOCUMENT.test(url.pathname) ? 'document'
    : url.searchParams.get('task') ? 'task'
      : url.pathname === '/app/projects' && url.searchParams.get('project') ? 'project'
        : /^\/app\/(bots|chats)\//.test(url.pathname) ? 'chat' : 'page';
  return kind === 'task' ? { kind, to, id: url.searchParams.get('task') } : { kind, to };
};

/** The site of an outside link, for its tooltip: "github.com". */
export const siteOf = (href) => {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const fileCard = (path, name, fileId) => {
  const dot = name.lastIndexOf('.');
  return { kind: 'file', path, name, ext: dot > 0 ? name.slice(dot + 1).toLowerCase() : '', ...(fileId ? { fileId } : {}) };
};

/**
 * What a finished run made, one card each: the documents it wrote (by id, the latest
 * title), the files it wrote on the computer (by path) and the files it handed over
 * (share_file: kept by BusyBee.Day, so they download also when the computer is gone).
 * Failed steps made nothing.
 */
export const runOutputs = (actions = []) => {
  const docs = new Map();
  const files = new Map();
  (actions || []).forEach((s) => {
    if (!s || s.ok === false) return;
    if ((s.tool === 'create_document' || s.tool === 'update_document') && s.document_id) {
      const type = s.doc_type || docs.get(s.document_id)?.type;
      docs.set(s.document_id, {
        kind: 'document', id: s.document_id, title: s.title || docs.get(s.document_id)?.title || '', ...(type ? { type } : {}),
      });
    } else if ((s.tool === 'write_file' || s.tool === 'browser_screenshot') && s.title) {
      if (!files.get(s.title)?.fileId) files.set(s.title, fileCard(s.title, s.title.split('/').pop()));
    } else if (s.tool === 'share_file') {
      (s.files || []).forEach((f) => {
        if (f && f.path) files.set(f.path, fileCard(f.path, f.name || f.path.split('/').pop(), f.file_id));
      });
    }
  });
  return [...docs.values(), ...files.values()];
};
